import { UserCell } from "@/components/ui/layout/table/userCell"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import ChatEmpty from "@images/empty/chat_empty.svg"
import { BoxStaffItemEntity } from "@modules/boxStaff/entities/boxStaffEntity"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { StaffColumnToSort } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffSidebar } from "@modules/boxStaff/hooks/useBoxStaffSidebar"
import { word } from "@modules/core/utils/i18n"
import { usePersistedBoxArchivedStaffMembers } from "@modules/members/boxTerminatedMembers/hooks/useBoxArchivedMembers"
import { ColumnSortingOrder } from "@modules/types/datatableTypes"
import { HUDatatable } from "@modules/ui/components/dataTables/huDatatable"
import { HeaderTemplate } from "@modules/ui/components/dataTables/templates/headerTemplate"
import { EmptyView } from "@modules/ui/components/emptyView"
import { createColumnHelper } from "@tanstack/react-table"
import dayjs from "dayjs"
import { useState } from "react"

type BoxArchivedStaffTableProps = {
  className?: string
} & React.ComponentProps<"div">

const columnHelper = createColumnHelper<BoxStaffItemEntity>()

export function BoxArchivedStaffTable({ className, ...props }: BoxArchivedStaffTableProps) {
  const [selectedArchivedMember, setSelectedArchivedMember] = useState<BoxStaffItemEntity>()
  const { navigateToStaffDetailsInfo } = useBoxStaffSidebar()

  const {
    data,
    totalElements,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage,
    hasNextPage,
    setSorting,
    sortBy,
    sortOrder,
    isSearching,
  } = usePersistedBoxArchivedStaffMembers()

  const columns = [
    columnHelper.accessor("username", {
      header: () => (
        <HeaderTemplate
          title={word("box.archived.table.staff.name")}
          variant={
            sortBy === StaffColumnToSort.NAME
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(StaffColumnToSort.NAME, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(StaffColumnToSort.NAME, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: member } }) => (
        <UserCell
          member={{
            avatar: member.avatar,
            firstname: member.firstname,
            lastname: member.lastname,
          }}
          subtitle={member.getUserName()}
          subtitleColor="neutral"
        />
      ),
    }),
    columnHelper.accessor("role", {
      header: () => (
        <HeaderTemplate
          title={word("box.archived.table.staff.lastPosition")}
          variant={
            sortBy === StaffColumnToSort.LAST_POSITION
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(StaffColumnToSort.LAST_POSITION, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(StaffColumnToSort.LAST_POSITION, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: member } }) => (
        <Text fontStyle="BM" className="text-textDefault-body">
          {displayRole(member.role)}
        </Text>
      ),
    }),
    columnHelper.accessor("creationDate", {
      header: () => (
        <HeaderTemplate
          title={word("box.archived.table.staff.inscriptionDate")}
          variant={
            sortBy === StaffColumnToSort.CREATION_DATE
              ? sortOrder === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(StaffColumnToSort.CREATION_DATE, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(StaffColumnToSort.CREATION_DATE, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: member } }) => (
        <Text fontStyle="BM" className="text-textDefault-body">
          {dayjs(member.creationDate).format("L")}
        </Text>
      ),
    }),
    columnHelper.accessor("archivedDate", {
      header: () => (
        <HeaderTemplate
          title={word("box.archived.table.staff.archivedDate")}
          variant={
            sortBy === StaffColumnToSort.ARCHIVED_DATE
              ? sortOrder === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(StaffColumnToSort.ARCHIVED_DATE, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(StaffColumnToSort.ARCHIVED_DATE, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: member } }) => (
        <Text fontStyle="BM" className="text-textDefault-body">
          {member.archivedDate ? dayjs(member.archivedDate).format("L") : "-"}
        </Text>
      ),
    }),
  ]

  const onSelectedArchivedStaff = (e: BoxStaffItemEntity) => {
    setSelectedArchivedMember(e)

    navigateToStaffDetailsInfo(e.id, {
      onHide: () => {
        setSelectedArchivedMember(undefined)
        return Promise.resolve(true)
      },
      replaceAllComponents: true,
    })
  }

  if (data.length === 0)
    return (
      <EmptyView
        className="w-full"
        image={{ src: ChatEmpty }}
        title={isSearching ? word("searching.list.empty.title") : word("archived.list.staff.empty.title")}
        description={
          isSearching ? word("searching.list.empty.description") : word("archived.list.staff.empty.description")
        }
        paddingType="In_table"
      />
    )

  return (
    <div className={cn("flex w-full flex-col px-6", className)} {...props}>
      <HUDatatable
        data={data}
        columns={columns}
        pageSize={pageSize}
        setPageSize={setPageSize}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        handleRowSelectionChange={(row: BoxStaffItemEntity) => onSelectedArchivedStaff(row)}
        selectedRowIndex={data.findIndex((item) => item.id === selectedArchivedMember?.id)}
        totalElements={totalElements}
      />
    </div>
  )
}
